import { BaseClickable, Box, Button, Icons, SolvTheme, Stack, Text } from '@solvhealth/jigsaw-v1';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useConsumerServiceCategories } from '~/components/AccountGetsMarried/constants/hooks/useConsumerServiceCategories';
import { SEARCH_MODAL } from '~/components/ModalController';
import ServiceCategoryCard from '~/components/Services/ServiceCategoryCard';
import { analyticsTrackEvent } from '~/core/analytics';
import {
  ACCOUNT_HOMEPAGE_CONSUMER_SERVICE_SECTION_LOADED,
  ACCOUNT_HOME_TOUCH_BROWSE_ALL_SERVICES,
  CONSUMER_SERVICES_ENTRYPOINT_ACCOUNT_HOME,
  TOUCH_SERVICE_SEARCH_FIELD,
} from '~/core/analytics/events';
import useToggleRuntimeModal from '~/hooks/runtime/useToggleRuntimeModal';

type ServicesSearchProps = {
  entrypoint: 'account homepage' | 'service categories list page' | 'service category details page';
};
export const ServicesSearch = ({ entrypoint }: ServicesSearchProps) => {
  const [_, toggleSearch] = useToggleRuntimeModal(SEARCH_MODAL);

  return (
    <BaseClickable
      border="1px solid"
      borderColor="gray-300"
      contentProps={{
        display: 'inline-block',
        width: '100%',
        maxWidth: { _: '100%', sm: '260px' },
      }}
      onClick={() => {
        analyticsTrackEvent(TOUCH_SERVICE_SEARCH_FIELD, { entrypoint });
        toggleSearch();
      }}
      p={1}
      width="100%"
    >
      <Stack alignItems="center" justifyContent="flex-start" row space={1}>
        <Icons.Search color="gray-300" size="sm" />
        <Text
          as="div"
          muted
          style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
        >
          Search symptom, service, test
        </Text>
      </Stack>
    </BaseClickable>
  );
};

export const ServicesSection = () => {
  useEffect(() => {
    analyticsTrackEvent(ACCOUNT_HOMEPAGE_CONSUMER_SERVICE_SECTION_LOADED);
  }, []);

  const { t } = useTranslation(['account', 'common']);

  const allCategories = useConsumerServiceCategories(CONSUMER_SERVICES_ENTRYPOINT_ACCOUNT_HOME);

  return (
    <Stack space={2} width="100%">
      <Text maxWidth={SolvTheme.breakpoints.lg} mx="auto" px={3} variant="h3" width="100%">
        {t('account:home.services.title')}
      </Text>

      <Box px={3} width="100%">
        <Box maxWidth="400px" width="100%">
          <ServicesSearch entrypoint="account homepage" />
        </Box>
      </Box>

      <Stack overflowX="scroll" overflowY="auto" pr={3} px={3} py={1} row space={2} width="100%">
        <Box
          display="grid"
          gridGap={SolvTheme.space[2]}
          sx={{
            gridAutoColumns: '150px',
            gridAutoFlow: 'column dense',
            gridTemplateRows: 'repeat(2, 1fr)',
          }}
        >
          {allCategories.map((category) => (
            <ServiceCategoryCard
              category={category}
              entrypoint="account home"
              key={category.slug}
              tinted
            />
          ))}
        </Box>
      </Stack>

      <Stack maxWidth={SolvTheme.breakpoints.lg} mx="auto" px={3} row width="100%">
        <Button
          event={ACCOUNT_HOME_TOUCH_BROWSE_ALL_SERVICES}
          href="/services"
          size="small"
          width={{ _: '100%', sm: 'auto' }}
        >
          Browse all services
        </Button>
      </Stack>
    </Stack>
  );
};
